// components/CheckoutPage.jsx
import React, { useState } from 'react';
import { CreditCard, Smartphone, Lock, Check, MapPin, User, Mail, Phone, ShoppingBag, ArrowLeft } from 'lucide-react';

const CheckoutPage = ({ config = {} }) => {
  // Hooks first
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('mpesa');

  // Safe config merge
  const defaultConfig = {
    activeDesign: 'side-by-side', // 'side-by-side', 'wizard', 'compact'
    title: 'Checkout',
    shippingTitle: 'Shipping Information',
    paymentTitle: 'Payment Method',
    reviewTitle: 'Review Your Order',
    emptyCartMessage: 'Your cart is empty',
    continueShoppingText: 'Continue Shopping',
    checkoutText: 'Complete Order',
    customStyles: {
      background: 'bg-gray-50',
      textColor: 'text-gray-900',
      buttonBg: 'bg-gray-900',
      buttonText: 'text-white',
      accentColor: 'text-green-600',
    },
  };

  const finalConfig = { ...defaultConfig, ...config };
  const { activeDesign, title, shippingTitle, paymentTitle, reviewTitle, emptyCartMessage, continueShoppingText, checkoutText, customStyles } = finalConfig;

  // Demo form & cart (replace with real state later)
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', address: '', city: '', zipCode: '', country: 'Kenya',
    mpesaPhone: '', cardNumber: '', cardName: '', cardExpiry: '', cardCvv: ''
  });

  const cartItems = [
    { id: 1, name: "Classic Cotton T-Shirt", price: 29.99, quantity: 2, image: "bg-gradient-to-br from-blue-100 to-blue-200" },
    { id: 2, name: "Slim Fit Denim Jeans", price: 79.99, quantity: 1, image: "bg-gradient-to-br from-indigo-100 to-purple-200" },
  ];

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = 10;
  const tax = subtotal * 0.16;
  const total = subtotal + shipping + tax;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(`Order placed via ${paymentMethod === 'mpesa' ? 'M-Pesa' : 'Card'}!`);
  };

  // Design 1: Side-by-Side (Desktop-first)
  const DesignSideBySide = () => (
    <div className={`min-h-screen ${customStyles.background}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left: Forms */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">{shippingTitle}</h2>
            <form className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="First Name" className="w-full p-3 border rounded" required />
                <input name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Last Name" className="w-full p-3 border rounded" required />
              </div>
              <input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="Email" className="w-full p-3 border rounded" required />
              <input name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="Phone" className="w-full p-3 border rounded" required />
              <input name="address" value={formData.address} onChange={handleInputChange} placeholder="Address" className="w-full p-3 border rounded" required />
              <div className="grid grid-cols-2 gap-4">
                <input name="city" value={formData.city} onChange={handleInputChange} placeholder="City" className="w-full p-3 border rounded" required />
                <input name="zipCode" value={formData.zipCode} onChange={handleInputChange} placeholder="ZIP Code" className="w-full p-3 border rounded" required />
              </div>
            </form>

            <h2 className="text-2xl font-bold text-gray-900 mt-8">{paymentTitle}</h2>
            <div className="grid grid-cols-2 gap-4">
              <button onClick={() => setPaymentMethod('mpesa')} className={`p-4 border rounded ${paymentMethod === 'mpesa' ? 'border-green-600 bg-green-50' : 'border-gray-300'}`}>
                M-Pesa
              </button>
              <button onClick={() => setPaymentMethod('card')} className={`p-4 border rounded ${paymentMethod === 'card' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'}`}>
                Card
              </button>
            </div>

            {paymentMethod === 'mpesa' && (
              <input name="mpesaPhone" value={formData.mpesaPhone} onChange={handleInputChange} placeholder="M-Pesa Phone (254...)" className="w-full p-3 border rounded mt-4" />
            )}
            {paymentMethod === 'card' && (
              <div className="space-y-4 mt-4">
                <input name="cardNumber" value={formData.cardNumber} onChange={handleInputChange} placeholder="Card Number" className="w-full p-3 border rounded" />
                <div className="grid grid-cols-2 gap-4">
                  <input name="cardExpiry" value={formData.cardExpiry} onChange={handleInputChange} placeholder="MM/YY" className="w-full p-3 border rounded" />
                  <input name="cardCvv" value={formData.cardCvv} onChange={handleInputChange} placeholder="CVV" className="w-full p-3 border rounded" />
                </div>
              </div>
            )}

            <button onClick={handleSubmit} className={`w-full mt-8 py-4 rounded font-semibold ${customStyles.buttonBg} ${customStyles.buttonText}`}>
              {checkoutText} - ${total.toFixed(2)}
            </button>
          </div>

          {/* Right: Summary */}
          <div className="lg:sticky lg:top-8">
            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="text-xl font-bold mb-6">Order Summary</h2>
              {cartItems.map(item => (
                <div key={item.id} className="flex gap-4 mb-4">
                  <div className={`w-16 h-16 rounded ${item.image}`} />
                  <div>
                    <h4 className="font-medium">{item.name}</h4>
                    <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                    <p className="font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                </div>
              ))}
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span>${shipping.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg mt-2">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Design 2: Multi-Step Wizard
  const DesignWizard = () => (
    <div className={`min-h-screen ${customStyles.background}`}>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Progress */}
        <div className="flex justify-between mb-12">
          {['Shipping', 'Payment', 'Review'].map((s, i) => (
            <div key={s} className="flex-1 text-center">
              <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center font-bold ${step > i + 1 ? 'bg-green-600 text-white' : step === i + 1 ? 'bg-gray-900 text-white' : 'bg-gray-300 text-gray-600'}`}>
                {step > i + 1 ? <Check size={20} /> : i + 1}
              </div>
              <p className="mt-2 text-sm font-medium">{s}</p>
            </div>
          ))}
        </div>

        {/* Content */}
        {step === 1 && (
          <div>
            <h2 className="text-3xl font-bold mb-6">{shippingTitle}</h2>
            {/* Shipping form fields */}
            <button onClick={() => setStep(2)} className={`w-full py-4 rounded ${customStyles.buttonBg} ${customStyles.buttonText}`}>
              Continue
            </button>
          </div>
        )}
        {step === 2 && (
          <div>
            <h2 className="text-3xl font-bold mb-6">{paymentTitle}</h2>
            {/* Payment selection & fields */}
            <button onClick={() => setStep(3)} className={`w-full py-4 rounded ${customStyles.buttonBg} ${customStyles.buttonText}`}>
              Review Order
            </button>
          </div>
        )}
        {step === 3 && (
          <div>
            <h2 className="text-3xl font-bold mb-6">{reviewTitle}</h2>
            {/* Summary */}
            <button onClick={handleSubmit} className={`w-full py-4 rounded ${customStyles.buttonBg} ${customStyles.buttonText}`}>
              {checkoutText}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Design 3: Compact Mobile-First
  const DesignCompact = () => (
    <div className={`min-h-screen ${customStyles.background} p-4`}>
      <div className="max-w-lg mx-auto bg-white rounded-xl shadow p-6">
        <h2 className="text-2xl font-bold mb-6">{title}</h2>
        {/* Compact forms, summary at bottom */}
        <button className={`w-full py-4 rounded ${customStyles.buttonBg} ${customStyles.buttonText}`}>
          {checkoutText}
        </button>
      </div>
    </div>
  );

  const renderDesign = () => {
    switch (activeDesign) {
      case 'side-by-side': return <DesignSideBySide />;
      case 'wizard': return <DesignWizard />;
      case 'compact': return <DesignCompact />;
      default: return <DesignSideBySide />;
    }
  };

  return renderDesign();
};

export default CheckoutPage;